import { Controller } from '@hotwired/stimulus';
import { markInputValid, markInputInvalid } from '@/services/form.services';

declare global {
    interface Window {
        contactFormRecaptchaCallback?: (token: string) => void;
        contactFormRecaptchaExpired?: () => void;
        grecaptcha?: unknown;
    }
}

export class ContactFormController extends Controller {
    declare readonly formTarget: HTMLFormElement;
    declare readonly emailTarget: HTMLInputElement;
    declare readonly hasEmailTarget: boolean;
    declare readonly emailErrorTargets: HTMLInputElement[];
    declare readonly messageTarget: HTMLTextAreaElement;
    declare readonly messageErrorTargets: HTMLInputElement[];
    declare readonly submitButtonTarget: HTMLButtonElement;
    declare readonly hasSubmitButtonTarget: boolean;
    declare readonly recaptchaTarget: HTMLElement;
    declare readonly hasRecaptchaTarget: boolean;
    declare readonly recaptchaWarningTarget: HTMLElement;
    declare readonly hasRecaptchaWarningTarget: boolean;

    private turboFrame: HTMLElement | null = null;
    private heightCleanupTimer: number | null = null;
    private recaptchaToken: string | null = null;
    private recaptchaFallbackMode = false;
    private recaptchaReadyCheckTimer: number | null = null;

    static targets = ['form', 'email', 'emailError', 'message', 'messageError', 'submitButton', 'recaptcha', 'recaptchaWarning'];

    declare readonly recaptchaSiteKeyValue: string;
    declare readonly userAuthenticatedValue: boolean;

    static values = {
        recaptchaSiteKey: String,
        userAuthenticated: Boolean,
    };

    connect(): void {
        this.turboFrame = this.element.closest('turbo-frame');

        // Listen for turbo:before-morph to preserve height during morphing
        if (this.turboFrame) {
            this.turboFrame.addEventListener('turbo:before-morph', this.handleBeforeMorph);
        }

        // Initialize reCAPTCHA for anonymous users
        this.initializeRecaptcha();
        this.updateSubmitButtonState();
    }

    disconnect(): void {
        if (this.turboFrame) {
            this.turboFrame.removeEventListener('turbo:before-morph', this.handleBeforeMorph);
        }

        if (this.heightCleanupTimer) {
            clearTimeout(this.heightCleanupTimer);
        }

        if (this.recaptchaReadyCheckTimer) {
            clearTimeout(this.recaptchaReadyCheckTimer);
            this.recaptchaReadyCheckTimer = null;
        }
    }

    handleBeforeMorph = (_event: Event): void => {
        // Preserve height before morphing occurs
        this.preserveFrameHeight();
    };

    submit(): void {
        if (!this.validate()) {
            return;
        }

        if (this.requiresRecaptcha() && !this.recaptchaFallbackMode && !this.isRecaptchaCompleted()) {
            alert('Please complete reCAPTCHA verification before sending your message.');
            return;
        }

        if (this.recaptchaFallbackMode) {
            this.appendHiddenField('recaptcha_fallback', 'native_unavailable');
        }

        this.preserveFrameHeight();
        this.showLoadingIndicator();
        this.formTarget.requestSubmit();
    }

    validate = (): boolean => {
        let emailValid = true;

        // Only validate email for anonymous users
        if (!this.userAuthenticatedValue && this.hasEmailTarget) {
            emailValid = this.validateEmail();
        }

        const messageValid = this.validateMessage();

        return emailValid && messageValid;
    };

    validateEmail = (): boolean => {
        if (!this.hasEmailTarget || !this.emailTarget) {
            return true; // Skip validation if no email field (authenticated users)
        }

        if (!this.emailTarget.validity.valid) {
            markInputInvalid(this.emailTarget, this.emailErrorTargets);
            this.emailTarget.addEventListener('input', this.validateEmail);

            return false;
        } else {
            markInputValid(this.emailTarget, this.emailErrorTargets);
            this.emailTarget.removeEventListener('input', this.validateEmail);

            return true;
        }
    };

    validateMessage = (): boolean => {
        if (!this.messageTarget.validity.valid) {
            markInputInvalid(this.messageTarget, this.messageErrorTargets);
            this.messageTarget.addEventListener('input', this.validateMessage);

            return false;
        } else {
            markInputValid(this.messageTarget, this.messageErrorTargets);
            this.messageTarget.removeEventListener('input', this.validateMessage);

            return true;
        }
    };

    preserveFrameHeight = (): void => {
        if (this.turboFrame) {
            // Clear any existing cleanup timer
            if (this.heightCleanupTimer) {
                clearTimeout(this.heightCleanupTimer);
            }

            // Get current height and set it as minimum height
            const currentHeight = this.turboFrame.offsetHeight;
            this.turboFrame.style.minHeight = `${currentHeight}px`;

            // Add a smooth transition class if not already present
            if (!this.turboFrame.classList.contains('height-transition')) {
                this.turboFrame.classList.add('height-transition');
            }

            // Remove the minimum height after the transition completes
            this.heightCleanupTimer = window.setTimeout(() => {
                if (this.turboFrame) {
                    this.turboFrame.style.minHeight = '';
                }
                this.heightCleanupTimer = null;
            }, 1000); // Give enough time for the slide-up animation (0.6s) plus buffer
        }
    };

    showLoadingIndicator = (): void => {
        const template = document.getElementById("spinner-template");
        if (template instanceof HTMLTemplateElement) {
            const clone = template.content.cloneNode(true);
            this.element.appendChild(clone);
        }
    };

    initializeRecaptcha = (): void => {
        // Only initialize reCAPTCHA for anonymous users
        if (this.requiresRecaptcha()) {
            // Set up global callbacks for this form instance
            window.contactFormRecaptchaCallback = this.onRecaptchaSuccess;
            window.contactFormRecaptchaExpired = this.onRecaptchaExpired;
            this.ensureRecaptchaScript();
            this.scheduleRecaptchaReadyCheck();
        }
    };

    onRecaptchaSuccess = (token: string): void => {
        this.recaptchaToken = token;
        this.updateSubmitButtonState();
    };

    onRecaptchaExpired = (): void => {
        this.recaptchaToken = null;
        this.updateSubmitButtonState();
    };

    isRecaptchaCompleted = (): boolean => {
        // For authenticated users, reCAPTCHA is not required
        if (this.userAuthenticatedValue) {
            return true;
        }

        // For anonymous users, check if reCAPTCHA is required and completed
        if (this.requiresRecaptcha() && !this.recaptchaFallbackMode) {
            return this.recaptchaToken !== null;
        }

        // If no reCAPTCHA is configured, consider it valid
        return true;
    };

    updateSubmitButtonState = (): void => {
        if (!this.hasSubmitButtonTarget) {return;}

        // For authenticated users, submit is always enabled
        if (this.userAuthenticatedValue) {
            this.submitButtonTarget.disabled = false;
            return;
        }

        // For anonymous users, require reCAPTCHA completion if configured
        if (this.requiresRecaptcha()) {
            this.submitButtonTarget.disabled = !this.isRecaptchaCompleted();
        } else {
            this.submitButtonTarget.disabled = false;
        }
    };

    private requiresRecaptcha(): boolean {
        return !this.userAuthenticatedValue && Boolean(this.recaptchaSiteKeyValue) && this.hasRecaptchaTarget;
    }

    private ensureRecaptchaScript(): void {
        if (document.querySelector('script[src*="recaptcha/api.js"]')) {
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }

    private scheduleRecaptchaReadyCheck(): void {
        if (this.recaptchaReadyCheckTimer) {
            clearTimeout(this.recaptchaReadyCheckTimer);
        }

        this.recaptchaReadyCheckTimer = window.setTimeout(() => {
            this.enableRecaptchaFallbackIfNeeded();
            this.recaptchaReadyCheckTimer = null;
        }, 2500);
    }

    private isHotwireNativeWebView(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('hotwire native') || ua.includes('turbo native');
    }

    private enableRecaptchaFallbackIfNeeded(): void {
        if (!this.requiresRecaptcha() || !this.isHotwireNativeWebView() || typeof window.grecaptcha !== 'undefined') {
            return;
        }

        this.recaptchaFallbackMode = true;
        this.recaptchaToken = null;
        this.setRecaptchaWarning(
            'reCAPTCHA did not load in this in-app view. You can still submit, but open this page in your browser if verification fails.'
        );
        this.updateSubmitButtonState();
    }

    private setRecaptchaWarning(message: string): void {
        if (!this.hasRecaptchaWarningTarget) {
            return;
        }

        this.recaptchaWarningTarget.textContent = message;
        this.recaptchaWarningTarget.classList.remove('hidden');
    }

    private appendHiddenField(name: string, value: string): void {
        const existing = this.formTarget.querySelector(`input[type="hidden"][name="${name}"]`) as HTMLInputElement | null;
        if (existing) {
            existing.value = value;
            return;
        }

        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = name;
        hiddenInput.value = value;
        this.formTarget.appendChild(hiddenInput);
    }
}
